// Catalog.ID Client — Agent API
// ============================================================================

import { api } from './client';
import type {
    CreateAgentRequest, CreateAgentResponse,
    AgentSummary, AgentDetail,
    UpdateAgentRequest,
    CreateApiKeyRequest, CreateApiKeyResponse,
    ApiKeySummary,
    RefreshTokenSummary,
} from './types';

export interface AgentScopeDef {
    id: string;
    label: string;
    description: string;
    /** True if this scope only makes sense for an agent whose principal is a delegate with owner/admin role. */
    requiresOrgManagement?: boolean;
}

/**
 * The full set of agent scopes recognized by the server (§3c.5.2).
 * Must stay in sync with `AgentScopes.ValidScopes` in
 * identity/server/src/CatalogId.Server/Controllers/AgentController.cs.
 */
export const AGENT_SCOPES: AgentScopeDef[] = [
    { id: 'registration_signing', label: 'Sign Registry registrations', description: 'Sign Registry registration claims on behalf of the principal.' },
    { id: 'attribution_signing',  label: 'Sign Registry attributions',  description: 'Sign Registry attribution claims (requires a published signing key).' },
    { id: 'file_upload',          label: 'Upload files',           description: 'Upload files to Catalog storage.' },
    { id: 'chat',                 label: 'Chat (read + write)',    description: 'Send and receive messages in chat.' },
    { id: 'chat_readonly',        label: 'Chat (read-only)',       description: 'Monitor chat channels without sending messages.' },
    { id: 'org_key_rotation',     label: 'Org key rotation',       description: 'Rotate the organization encryption keys on delegate departure. Requires principal to be an owner/admin delegate.', requiresOrgManagement: true },
    { id: 'org_key_flattening',   label: 'Org key flattening',     description: 'Flatten the organization key chain. Requires principal to be an owner/admin delegate.', requiresOrgManagement: true },
];

/**
 * Filter the scope list to those that make sense for the current principal.
 * Org-scoped operations are hidden unless the caller is an owner/admin delegate,
 * since only those roles hold the organization's wrapped key chain (§3a.4, §3a.5).
 */
export function filterAgentScopes(canManageOrgKeys: boolean): AgentScopeDef[] {
    return AGENT_SCOPES.filter(s => !s.requiresOrgManagement || canManageOrgKeys);
}

export async function createAgent(req: CreateAgentRequest): Promise<CreateAgentResponse> {
    return api.post('/api/agent', req);
}

export async function listAgents(): Promise<AgentSummary[]> {
    return api.get('/api/agent');
}

export async function getAgent(agentCid: string): Promise<AgentDetail> {
    return api.get(`/api/agent/${agentCid}`);
}

export async function updateAgent(agentCid: string, req: UpdateAgentRequest): Promise<void> {
    return api.put(`/api/agent/${agentCid}`, req);
}

export async function terminateAgent(agentCid: string): Promise<void> {
    return api.del(`/api/agent/${agentCid}`);
}

export async function createApiKey(agentCid: string, req: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    return api.post(`/api/agent/${agentCid}/apikeys`, req);
}

export async function listApiKeys(agentCid: string): Promise<ApiKeySummary[]> {
    return api.get(`/api/agent/${agentCid}/apikeys`);
}

export async function revokeApiKey(agentCid: string, keyId: number): Promise<void> {
    return api.del(`/api/agent/${agentCid}/apikeys/${keyId}`);
}

export async function listRefreshTokens(agentCid: string): Promise<RefreshTokenSummary[]> {
    return api.get(`/api/agent/${agentCid}/tokens`);
}

export async function revokeRefreshToken(agentCid: string, tokenId: number): Promise<void> {
    return api.del(`/api/agent/${agentCid}/tokens/${tokenId}`);
}
